import { useCallback, useEffect, useRef, useState } from 'react';
import type { ZodType } from 'zod';
import type { ErrorCode } from '@unit/shared';
import { ApiClientError, apiGet } from './api.js';

export type ApiState<T> =
  | { kind: 'loading' }
  | { kind: 'ready'; data: T }
  | { kind: 'error'; code: ErrorCode; requestId: string };

/**
 * A GET against the API, parsed through the same shared schema the server
 * validated its response against.
 *
 * The failure branch carries the typed code and the request id rather than a
 * message: the code selects a translated string from the i18next catalogue, and
 * the request id is what a user quotes when something needs investigating.
 *
 * Pass `path: null` to hold off — for a route whose parameter has not resolved
 * yet — rather than calling the hook conditionally.
 */
export function useApi<T>(
  path: string | null,
  schema: ZodType<T>,
): ApiState<T> & { reload: () => void } {
  const [state, setState] = useState<ApiState<T>>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (path === null) {
      setState({ kind: 'loading' });
      lastPath.current = null;
      return;
    }

    let cancelled = false;
    // A *reload* of the same path keeps the current data on screen until the
    // new data lands. Dropping to `loading` would unmount everything rendered
    // from that data — including a form's own "created, invite sent" state —
    // just to show a spinner for 200ms. A *new* path is a different screen and
    // starts from loading, so stale rows never appear under the wrong heading.
    if (lastPath.current !== path) {
      setState({ kind: 'loading' });
      lastPath.current = path;
    }

    apiGet(path, schema)
      .then((data) => {
        if (!cancelled) setState({ kind: 'ready', data });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const apiError = error instanceof ApiClientError ? error : null;
        setState({
          kind: 'error',
          code: apiError?.code ?? 'internal',
          requestId: apiError?.requestId ?? '',
        });
      });

    return () => {
      cancelled = true;
    };
    // `schema` belongs in here honestly. It is a module-level constant at every
    // call site, so it is referentially stable and costs nothing; a schema built
    // inline in a component would re-fetch every render, which is a bug worth
    // failing loudly rather than a dependency worth suppressing.
  }, [path, attempt, schema]);

  return { ...state, reload };
}
